import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import AuthAPI from '../api/auth';
import {
  getOtelloConfig,
  isAccountScopedLocation,
  safeReloadWindow,
} from './browserNavigation';
import { clearSafeTimeout, scheduleSafeTimeout } from './browserTimers';
import { getWindowGlobal } from './browserRuntime';
import { INBOX_STORE_FETCH_INBOX_LABELS_ACTION } from './inboxStoreContract';
import { CONVERSATION_PLACEMENT_SOURCE } from './conversationPlacementState';
import {
  AI_SCHEDULED_STATE_ATTRIBUTE_KEY,
  AI_SCHEDULED_STATE_REVISION_ATTRIBUTE_KEY,
  isStaleConversationAiScheduledStateSnapshot,
} from './conversationScheduledItems';
import { preserveRealtimeUnreadState } from './conversationUnreadState';

const NEW_EMAIL_MESSAGE_BADGE_DURATION_MS = 7000;
const SET_CONVERSATION_NEW_MESSAGE_BADGE = 'SET_CONVERSATION_NEW_MESSAGE_BADGE';
const CLEAR_CONVERSATION_NEW_MESSAGE_BADGE =
  'CLEAR_CONVERSATION_NEW_MESSAGE_BADGE';

const isIncomingRealtimeMessage = (message = {}) =>
  message?.message_type === 0 ||
  message?.message_type === 'incoming' ||
  message?.message_type_before_type_cast === 0;

const resolveRealtimeInboxId = (payload = {}) =>
  payload?.inbox_id || payload?.inboxId || payload?.inbox?.id || null;

const normalizeRealtimeTimestamp = (value = null) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    return numericValue > 9999999999
      ? Math.floor(numericValue / 1000)
      : numericValue;
  }

  const parsedValue = Date.parse(value);
  return Number.isFinite(parsedValue) ? Math.floor(parsedValue / 1000) : 0;
};

const resolveLatestRealtimeMessage = (conversation = {}) => {
  const messages = Array.isArray(conversation?.messages)
    ? conversation.messages
    : [];
  if (messages.length) {
    return messages[messages.length - 1];
  }

  return (
    conversation?.last_non_activity_message ||
    conversation?.last_message ||
    null
  );
};

const resolveLatestIncomingRealtimeMessage = (conversation = {}) => {
  const messages = Array.isArray(conversation?.messages)
    ? conversation.messages
    : [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (isIncomingRealtimeMessage(messages[index])) {
      return messages[index];
    }
  }

  const fallbackMessage = resolveLatestRealtimeMessage(conversation);
  return isIncomingRealtimeMessage(fallbackMessage) ? fallbackMessage : null;
};

const resolveRealtimeConversationLastIncomingAt = (conversation = {}) => {
  const directTimestamp = normalizeRealtimeTimestamp(
    conversation?.last_incoming_at || conversation?.lastIncomingAt
  );
  if (directTimestamp) {
    return directTimestamp;
  }

  return normalizeRealtimeTimestamp(
    resolveLatestIncomingRealtimeMessage(conversation)?.created_at
  );
};

const hasSameRealtimeMessageId = (first = {}, second = {}) => {
  const firstId = first?.id || first?.echo_id;
  const secondId = second?.id || second?.echo_id;
  if (firstId === null || firstId === undefined || firstId === '') {
    return false;
  }
  if (secondId === null || secondId === undefined || secondId === '') {
    return false;
  }

  return String(firstId) === String(secondId);
};

const EMAIL_CHANNEL_TYPE = 'Channel::Email';
const WHATSAPP_CHANNEL_TYPES = new Set([
  'Channel::Whatsapp',
  'Channel::WhatsappBusiness',
]);

// La lista visibile e' solo una delle pagine: una conversazione puo' vivere
// solo nelle pagine in cache (l'utente ha cambiato cartella), che e' proprio
// il caso in cui l'indicatore va scritto anche li'.
const findRealtimeConversation = ({
  conversationId = null,
  store = null,
} = {}) => {
  const getConversationById = store?.getters?.getConversationById;
  const conversation =
    typeof getConversationById === 'function'
      ? getConversationById(conversationId)
      : null;
  if (conversation) {
    return conversation;
  }

  const getCachedConversationById = store?.getters?.getCachedConversationById;
  return typeof getCachedConversationById === 'function'
    ? getCachedConversationById(conversationId) || null
    : null;
};

// Sidekiq can deliver two conversation.updated jobs out of enqueue order.
// Keep every other field from the incoming snapshot, but never let an older
// AI scheduled-state revision overwrite the state already shown in the row.
// The staleness predicate is shared with the store merge, which guards the
// remaining snapshot paths (status_changed/assignee/read and REST responses).
const preserveNewestAiScheduledState = ({
  currentConversation = null,
  incomingConversation = null,
} = {}) => {
  if (!incomingConversation || typeof incomingConversation !== 'object') {
    return incomingConversation;
  }

  if (
    !isStaleConversationAiScheduledStateSnapshot({
      currentConversation,
      incomingConversation,
    })
  ) {
    return incomingConversation;
  }

  const currentAttributes = currentConversation.additional_attributes || {};
  return {
    ...incomingConversation,
    pending_preview: currentConversation.pending_preview,
    additional_attributes: {
      ...(incomingConversation.additional_attributes || {}),
      [AI_SCHEDULED_STATE_ATTRIBUTE_KEY]:
        currentAttributes[AI_SCHEDULED_STATE_ATTRIBUTE_KEY],
      [AI_SCHEDULED_STATE_REVISION_ATTRIBUTE_KEY]:
        currentAttributes[AI_SCHEDULED_STATE_REVISION_ATTRIBUTE_KEY],
    },
  };
};

// Tetto di attesa per una generazione bozza senza esito: oltre, lo spinner
// "Otello sta scrivendo…" viene spento dal client. Piu' largo del caso peggiore
// reale (generazione LLM + retry del job a 5s), cosi' scatta solo su eventi
// davvero persi.
const DRAFT_GENERATION_WATCHDOG_MS = 3 * 60 * 1000;

const draftGenerationWatchdogKey = ({ conversationId, generationId }) =>
  `${conversationId}::${generationId || 'legacy'}`;

// Alcuni eventi (draft.auto_generated) non portano il canale: senza risolverlo
// non si puo' sapere quale indicatore programmato accendere.
const resolveRealtimeConversationChannelType = ({
  conversationId = null,
  store = null,
} = {}) => {
  const conversation = findRealtimeConversation({ conversationId, store });
  const channelType =
    conversation?.channel || conversation?.inbox?.channel_type || null;
  if (channelType) {
    return channelType;
  }

  const inboxId = resolveRealtimeInboxId(conversation || {});
  if (!inboxId) {
    return null;
  }

  const getInbox = store?.getters?.['inboxes/getInbox'];
  const inbox = typeof getInbox === 'function' ? getInbox(inboxId) : null;
  return inbox?.channel_type || null;
};

const isEmailRealtimeConversation = ({ conversation = {}, store = null }) => {
  if (
    conversation?.channel === EMAIL_CHANNEL_TYPE ||
    conversation?.inbox?.channel_type === EMAIL_CHANNEL_TYPE
  ) {
    return true;
  }

  const inboxId = resolveRealtimeInboxId(conversation);
  if (!inboxId) {
    return false;
  }

  const getInbox = store?.getters?.['inboxes/getInbox'];
  const inbox = typeof getInbox === 'function' ? getInbox(inboxId) : null;
  return inbox?.channel_type === EMAIL_CHANNEL_TYPE;
};

// Un backend piu' vecchio (rolling deploy) non manda il campo: in quel caso
// l'indicatore non si tocca, spegnerlo per sbaglio nasconderebbe gli invii
// ancora in coda sulla stessa conversazione.
const readRemainingScheduledFlag = (data = {}) =>
  Object.prototype.hasOwnProperty.call(data || {}, 'has_remaining_scheduled')
    ? Boolean(data.has_remaining_scheduled)
    : null;

// Firma dei soli campi che riordinano o filtrano la lista conversazioni.
// I flag indicatore (programmato, bozza AI, risposta di Otello) fanno ora
// partire un conversation.updated a ogni email in arrivo: invalidare la cache
// anche per loro ricaricava la cartella a ogni giro e cancellava la scrittura
// in cache appena fatta dalle mutation degli indicatori.
const buildConversationListSignature = (conversation = null) => {
  if (!conversation || typeof conversation !== 'object') {
    return null;
  }

  const meta = conversation.meta || {};
  const labels = Array.isArray(conversation.labels)
    ? conversation.labels.map(String).sort()
    : [];

  return JSON.stringify({
    status: conversation.status ?? null,
    assigneeId: meta.assignee?.id ?? conversation.assignee_id ?? null,
    teamId: meta.team?.id ?? conversation.team_id ?? null,
    labels,
    priority: conversation.priority ?? null,
    snoozedUntil: conversation.snoozed_until ?? null,
    customAttributes: conversation.custom_attributes ?? null,
    // Chiavi di ordinamento della lista (SORT_OPTIONS in
    // store/modules/conversations/helpers.js) e del filtro non gestite.
    timestamp: normalizeRealtimeTimestamp(conversation.timestamp),
    createdAt: normalizeRealtimeTimestamp(conversation.created_at),
    lastIncomingAt: normalizeRealtimeTimestamp(conversation.last_incoming_at),
    waitingSince: normalizeRealtimeTimestamp(conversation.waiting_since),
    firstReplyCreatedAt: normalizeRealtimeTimestamp(
      conversation.first_reply_created_at
    ),
    agentLastSeenAt: normalizeRealtimeTimestamp(
      conversation.agent_last_seen_at
    ),
    // Cambia l'icona della riga: una pagina in cache che non lo vede resta
    // vecchia, perche' UPDATE_CONVERSATION scrive in cache solo le righe
    // gia' presenti in allConversations.
    hasAiReply: conversation.has_ai_reply ?? null,
  });
};

const conversationUpdateAffectsList = ({
  currentConversation = null,
  incomingConversation = null,
} = {}) => {
  const currentSignature = buildConversationListSignature(currentConversation);
  // Senza uno stato locale con cui confrontarsi non si puo' escludere nulla.
  if (currentSignature === null) {
    return true;
  }

  return (
    currentSignature !== buildConversationListSignature(incomingConversation)
  );
};

const shouldMarkConversationUpdateAsNewEmail = ({
  currentConversation = null,
  incomingConversation = {},
  store = null,
} = {}) => {
  if (
    !incomingConversation?.id ||
    !isEmailRealtimeConversation({ conversation: incomingConversation, store })
  ) {
    return false;
  }

  const latestIncomingMessage =
    resolveLatestIncomingRealtimeMessage(incomingConversation);
  const latestMessage = resolveLatestRealtimeMessage(incomingConversation);
  if (latestMessage && !isIncomingRealtimeMessage(latestMessage)) {
    return false;
  }

  const incomingLastIncomingAt =
    resolveRealtimeConversationLastIncomingAt(incomingConversation);
  if (!incomingLastIncomingAt) {
    return false;
  }

  const currentLastIncomingAt =
    resolveRealtimeConversationLastIncomingAt(currentConversation);
  if (incomingLastIncomingAt > currentLastIncomingAt) {
    return true;
  }

  return Boolean(
    latestIncomingMessage &&
    !hasSameRealtimeMessageId(
      latestIncomingMessage,
      resolveLatestRealtimeMessage(currentConversation)
    ) &&
    currentLastIncomingAt === 0
  );
};

const hasHydratedEmailValue = (value = null) => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  return Boolean(
    value &&
    typeof value === 'object' &&
    (value.full || value.reply || value.quoted)
  );
};

const hasRenderableEmailContent = (message = {}) => {
  const email = message?.content_attributes?.email;
  if (!email || message?.hydrationPending === true) {
    return false;
  }

  return Boolean(
    hasHydratedEmailValue(email.html_content) ||
    hasHydratedEmailValue(email.text_content)
  );
};

const isEmailMessageMissingHydratedBody = (message = {}) => {
  const email = message?.content_attributes?.email;
  return Boolean(email) && !hasRenderableEmailContent(message);
};

const buildRealtimeHydrationKey = (accountId = null, conversationId = null) =>
  `${accountId ?? 'unknown'}:${conversationId ?? 'unknown'}`;

const hasActiveRenderableEmailMessage = ({
  conversationId = null,
  message = {},
  store = null,
} = {}) => {
  const selected = store?.getters?.getSelectedChat || {};
  const getConversationById = store?.getters?.getConversationById;
  let conversation = null;
  if (selected && Number(selected.id) === Number(conversationId)) {
    conversation = selected;
  } else if (typeof getConversationById === 'function') {
    conversation = getConversationById(conversationId);
  }
  if (!conversation) return false;

  const candidateIds = [message?.id, message?.echo_id]
    .filter((id) => id !== null && id !== undefined && id !== '')
    .map((id) => String(id));
  if (!candidateIds.length) {
    return false;
  }

  return (conversation.messages || []).some((storeMessage) => {
    const storeIds = [storeMessage?.id, storeMessage?.echo_id]
      .filter((id) => id !== null && id !== undefined && id !== '')
      .map((id) => String(id));
    const isMatchingMessage = storeIds.some((id) => candidateIds.includes(id));
    return isMatchingMessage && hasRenderableEmailContent(storeMessage);
  });
};

const typingTimerKey = (conversationId, user) =>
  JSON.stringify([
    String(conversationId),
    String(user?.id),
    typeof user?.type,
    user?.type,
  ]);

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = getOtelloConfig();
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = new Map();
    this.SyncTimers = {};
    // Timers e cooldown per snapshot conversazioni (dedupe tra burst di eventi)
    this.SnapshotTimers = {};
    this.SnapshotCooldown = {};
    this.PrefetchTimers = {};
    this.PrefetchCooldown = {};
    this.NewMessageBadgeTimers = {};
    this.draftGenerationWatchdogs = {};
    this.activePrefetchCount = 0;
    this.isDisconnected = false;
    this.labelRefreshTimers = {};
    this.inboxRefreshTimer = null;
    this.realtimeResyncTimer = null;
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'message.classified': this.onMessageClassified,
      'draft.generation_started': this.onDraftGenerationStarted,
      'draft.auto_generated': this.onDraftAutoGenerated,
      'draft.generation_failed': this.onDraftGenerationFailed,
      'scheduled_email.created': this.onScheduledEmailCreated,
      'scheduled_email.failed': this.onScheduledEmailFailed,
      'scheduled_email.sent': this.onScheduledEmailSent,
      'scheduled_message.created': this.onScheduledMessageCreated,
      'scheduled_message.failed': this.onScheduledMessageFailed,
      'scheduled_message.sent': this.onScheduledMessageSent,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'realtime.resync_required': this.onRealtimeResyncRequired,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = (options) => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT, options);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  onRealtimeResyncRequired = () => {
    if (this.isDisconnected || this.realtimeResyncTimer) return;

    this.realtimeResyncTimer = scheduleSafeTimeout(() => {
      this.realtimeResyncTimer = null;
      if (!this.isDisconnected) this.onReconnect({ fullSync: true });
    }, 1000);
  };

  isAValidEvent = (data, event) => {
    // Redis recovery is sent on each already-authorized stream. A user stream
    // spans accounts, so reconcile only the account currently open in the UI.
    if (
      event === 'realtime.resync_required' &&
      data?.reason === 'redis_reconnected' &&
      data?.account_id == null
    ) {
      return true;
    }
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = (data) => {
    const { conversation_id: conversationId } = data;
    const hasLocalHydratedEmail = hasActiveRenderableEmailMessage({
      conversationId,
      message: data,
      store: this.app.$store,
    });
    const needsHydration =
      isEmailMessageMissingHydratedBody(data) && !hasLocalHydratedEmail;
    this.app.$store.dispatch(
      'updateMessage',
      needsHydration ? { ...data, hydrationPending: true } : data
    );

    if (!needsHydration) return;
    const selected = this.app.$store.getters.getSelectedChat || {};
    if (Number(selected.id) === Number(conversationId)) {
      this.scheduleActiveConversationSync(conversationId);
    } else {
      this.schedulePrefetch(conversationId, { bypassCooldown: true });
    }
  };

  onMessageClassified = (data) => {
    const { conversation_id: conversationId, classification } = data;
    this.app.$store.commit('SET_CONVERSATION_CLASSIFICATION', {
      conversationId,
      classification,
    });
  };

  onDraftGenerationStarted = (data) => {
    const { conversation_id: conversationId, generation_id: generationId } =
      data;

    this.app.$store.commit('SET_CONVERSATION_GENERATING_DRAFT', {
      conversationId,
      generationId,
      isGenerating: true,
    });
    this.scheduleDraftGenerationWatchdog({ conversationId, generationId });
  };

  // "Otello sta scrivendo…" e' un flag solo client: se l'evento di
  // completamento o fallimento si perde in un buco WebSocket, nessun refetch
  // lo spegne (le chiavi locali sopravvivono ai merge) e resta acceso fino al
  // reload. Il watchdog e' il limite superiore: spegne SOLO la generazione che
  // l'ha armato (la mutation scarta per generationId), mai una piu' recente.
  scheduleDraftGenerationWatchdog = ({ conversationId, generationId }) => {
    const windowGlobal = getWindowGlobal();
    if (typeof windowGlobal?.setTimeout !== 'function') return;

    const key = draftGenerationWatchdogKey({ conversationId, generationId });
    this.clearDraftGenerationWatchdog(key);
    this.draftGenerationWatchdogs[key] = windowGlobal.setTimeout(() => {
      delete this.draftGenerationWatchdogs[key];
      this.app.$store.commit('SET_CONVERSATION_GENERATING_DRAFT', {
        conversationId,
        generationId,
        isGenerating: false,
      });
    }, DRAFT_GENERATION_WATCHDOG_MS);
  };

  clearDraftGenerationWatchdog = (key) => {
    const timer = this.draftGenerationWatchdogs[key];
    if (timer === undefined) return;

    delete this.draftGenerationWatchdogs[key];
    const windowGlobal = getWindowGlobal();
    if (typeof windowGlobal?.clearTimeout === 'function') {
      windowGlobal.clearTimeout(timer);
    }
  };

  // La chiusura con generationId spegne anche l'id legacy nello store
  // (buildDraftGenerationPatch li filtra entrambi): i watchdog seguono la
  // stessa regola per non lasciare armato un timer senza id.
  disarmDraftGenerationWatchdogs = ({ conversationId, generationId }) => {
    this.clearDraftGenerationWatchdog(
      draftGenerationWatchdogKey({ conversationId, generationId })
    );
    if (generationId) {
      this.clearDraftGenerationWatchdog(
        draftGenerationWatchdogKey({ conversationId, generationId: null })
      );
    }
  };

  onDraftAutoGenerated = (data) => {
    const {
      conversation_id: conversationId,
      draft,
      generation_id: generationId,
      message_id: messageId,
    } = data;
    const generationMetadata = generationId ? { generationId } : {};

    this.disarmDraftGenerationWatchdogs({ conversationId, generationId });
    // Emette un evento bus per notificare la RightSidebar
    emitter.emit(BUS_EVENTS.DRAFT_AUTO_GENERATED, {
      conversationId,
      messageId,
      draft,
      ...generationMetadata,
    });
    // Aggiorna lo store con la bozza auto-generata
    this.app.$store.commit('SET_CONVERSATION_AUTO_DRAFT', {
      conversationId,
      draft,
      ...generationMetadata,
    });
    // In sola bozza il flag programmato lo scrive il backend senza broadcast:
    // va acceso qui, ma solo per il canale della conversazione. Se il canale
    // non e' risolvibile non si accende niente, meglio di un indicatore falso.
    this.commitScheduledIndicatorForDraft(conversationId);
    // Cache la bozza per quando la conversazione non è in memoria
    // Utile se l'utente ha cambiato pagina e la conversazione non è più in allConversations
    this.app.$store.commit('CACHE_DRAFT_COMPLETION', {
      conversationId,
      draft,
      ...generationMetadata,
    });

    // Prefetch messaggi in background per allineare il cache /messages
    // anche quando la conversazione non è attiva al momento dell'evento.
    this.app.$store
      .dispatch('prefetchConversationMessages', { conversationId, force: true })
      .catch(() => {});
  };

  onDraftGenerationFailed = (data) => {
    const {
      conversation_id: conversationId,
      error_type: errorType,
      generation_id: generationId,
    } = data;
    const generationMetadata = generationId ? { generationId } : {};

    this.disarmDraftGenerationWatchdogs({ conversationId, generationId });
    // Reset stato generazione
    this.app.$store.commit('SET_CONVERSATION_GENERATING_DRAFT', {
      conversationId,
      isGenerating: false,
      ...generationMetadata,
    });

    // ai_needs_human segue lo stato persistito via conversation.updated:
    // una chiusura no_draft ritardata non deve riaccenderlo dopo la lettura.

    // Notifica UI tramite evento bus. errorType 'no_draft' = generazione
    // chiusa senza bozza: la sidebar lo usa per NON mostrare l'avviso.
    emitter.emit(BUS_EVENTS.DRAFT_GENERATION_FAILED, {
      conversationId,
      errorType,
    });
  };

  commitScheduledIndicatorForDraft = (conversationId) => {
    const channelType = resolveRealtimeConversationChannelType({
      conversationId,
      store: this.app.$store,
    });

    if (channelType === EMAIL_CHANNEL_TYPE) {
      this.app.$store.commit('SET_CONVERSATION_HAS_SCHEDULED_EMAIL', {
        conversationId,
        hasScheduledEmail: true,
      });
    } else if (WHATSAPP_CHANNEL_TYPES.has(channelType)) {
      this.app.$store.commit('SET_CONVERSATION_HAS_SCHEDULED_MESSAGE', {
        conversationId,
        hasScheduledMessage: true,
      });
    }
  };

  onScheduledEmailCreated = (data) => {
    const { conversation_id: conversationId, scheduled_email: scheduledEmail } =
      data;
    this.app.$store.commit('SET_CONVERSATION_HAS_SCHEDULED_EMAIL', {
      conversationId,
      hasScheduledEmail: true,
    });
    emitter.emit(BUS_EVENTS.SCHEDULED_EMAIL_CREATED, {
      conversationId,
      scheduledEmail,
    });
  };

  onScheduledEmailFailed = (data) => {
    const {
      conversation_id: conversationId,
      scheduled_email_id: scheduledEmailId,
      error,
    } = data;

    // L'invio e' fallito: l'indicatore resta acceso solo se la conversazione
    // ha ancora altri invii programmati.
    const remainingScheduled = readRemainingScheduledFlag(data);
    if (remainingScheduled !== null) {
      this.app.$store.commit('SET_CONVERSATION_HAS_SCHEDULED_EMAIL', {
        conversationId,
        hasScheduledEmail: remainingScheduled,
      });
    }
    emitter.emit(BUS_EVENTS.SCHEDULED_EMAIL_FAILED, {
      conversationId,
      scheduledEmailId,
      error,
    });
  };

  onScheduledEmailSent = (data) => {
    const { conversation_id: conversationId, scheduled_email: scheduledEmail } =
      data;

    // Stesso patto del ramo failed: senza il campo non si sa cosa resta in
    // coda, e spegnere l'indicatore nasconderebbe gli invii ancora vivi.
    const remainingScheduled = readRemainingScheduledFlag(data);
    if (remainingScheduled !== null) {
      this.app.$store.commit('SET_CONVERSATION_HAS_SCHEDULED_EMAIL', {
        conversationId,
        hasScheduledEmail: remainingScheduled,
      });
      // L'email e' partita: resetta anche lo stato bozza AI in-memory
      // (autoDraft e has_pending_ai_draft) come fa il gemello WhatsApp. Solo
      // pero' se non resta nulla in coda: l'email appena partita puo' essere
      // quella scritta a mano, con la bozza AI ancora in attesa.
      if (!remainingScheduled) {
        this.app.$store.commit('CLEAR_CONVERSATION_AUTO_DRAFT', conversationId);
      }
    }
    emitter.emit(BUS_EVENTS.SCHEDULED_EMAIL_SENT, {
      conversationId,
      scheduledEmail,
    });
  };

  onScheduledMessageCreated = (data) => {
    const {
      conversation_id: conversationId,
      scheduled_message: scheduledMessage,
    } = data;
    this.app.$store.commit('SET_CONVERSATION_HAS_SCHEDULED_MESSAGE', {
      conversationId,
      hasScheduledMessage: true,
    });
    emitter.emit(BUS_EVENTS.SCHEDULED_MESSAGE_CREATED, {
      conversationId,
      scheduledMessage,
    });
  };

  onScheduledMessageFailed = (data) => {
    const {
      conversation_id: conversationId,
      scheduled_message_id: scheduledMessageId,
      error,
    } = data;

    // Il solo emit non spegne l'icona: la card non ascolta il bus.
    const remainingScheduled = readRemainingScheduledFlag(data);
    if (remainingScheduled !== null) {
      this.app.$store.commit('SET_CONVERSATION_HAS_SCHEDULED_MESSAGE', {
        conversationId,
        hasScheduledMessage: remainingScheduled,
      });
    }
    emitter.emit(BUS_EVENTS.SCHEDULED_MESSAGE_FAILED, {
      conversationId,
      scheduledMessageId,
      error,
    });
  };

  onScheduledMessageSent = (data) => {
    const {
      conversation_id: conversationId,
      scheduled_message: scheduledMessage,
    } = data;
    // Stesso patto del ramo failed: senza il campo non si sa cosa resta in
    // coda, e spegnere l'indicatore nasconderebbe gli invii ancora vivi.
    const remainingScheduled = readRemainingScheduledFlag(data);
    if (remainingScheduled !== null) {
      this.app.$store.commit('SET_CONVERSATION_HAS_SCHEDULED_MESSAGE', {
        conversationId,
        hasScheduledMessage: remainingScheduled,
      });
      // Il messaggio è stato inviato: resetta anche lo stato bozza AI in-memory
      // (autoDraft e has_pending_ai_draft) per evitare che l'icona AI rimanga
      // visibile, ma solo se non resta nulla in coda.
      if (!remainingScheduled) {
        this.app.$store.commit('CLEAR_CONVERSATION_AUTO_DRAFT', conversationId);
      }
    }
    emitter.emit(BUS_EVENTS.SCHEDULED_MESSAGE_SENT, {
      conversationId,
      scheduledMessage,
    });
  };

  onPresenceUpdate = (data) => {
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', {
      users: data.users,
      accountId: data.account_id,
    });
  };

  onConversationContactChange = (payload) => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = (payload) => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch(
        'updateConversation',
        this.buildRealtimeConversationUpdate(payload)
      );
    }
  };

  onConversationCreated = (data) => {
    const conversation = this.buildRealtimeConversationUpdate(data);
    this.app.$store.dispatch('addConversation', conversation);
    this.showNewEmailBadgeForConversation(data);

    // Invalida il cache conversazioni per l'inbox interessata così che un successivo
    // fetch non ripristini una lista obsoleta che non include la nuova conversazione
    try {
      const inboxId = data?.inbox_id || data?.inboxId || data?.inbox?.id;
      if (inboxId) {
        this.app.$store.dispatch('clearConversationCache', { inboxId });
      }
    } catch (_) {
      // ignore cache invalidation errors
    }
  };

  onConversationRead = (data) => {
    // Passa anche conversation.read dal merge realtime protetto: sync-channel lo usa
    // come read-state provider, mentre Rails storico lo usa anche per contact seen.
    this.app.$store.dispatch(
      'updateConversation',
      this.buildRealtimeConversationUpdate(data, {
        source: 'conversation-read',
      })
    );

    // Invalida la cache liste conversazioni per inbox email
    try {
      const inboxId = data?.inbox_id || data?.inboxId || data?.inbox?.id;
      if (inboxId) {
        const inbox = this.app.$store.getters['inboxes/getInbox'](inboxId);
        const isEmail = inbox?.channel_type === 'Channel::Email';
        if (isEmail) {
          this.app.$store.dispatch('clearConversationCache', { inboxId });
          // Refresh label unread status (debounced)
          this.scheduleLabelsUnreadRefresh(inboxId);
        } else {
          // Per inbox non-email: refresh debounced dello stato unread
          this.scheduleInboxUnreadRefresh();
        }
      }
    } catch (_) {
      // ignore cache invalidation errors
    }

    // Pianifica snapshot metadati conversazione per allineare eventuali contatori/campi
    this.scheduleConversationSnapshot(data?.id, { conversation: data });
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = (data) => {
    // 🛡️ PROTEZIONE: Gestisce il caso in cui conversation sia undefined
    // Questo può accadere quando il WebSocket invia un payload incompleto
    const { conversation = {}, conversation_id: conversationId } = data;
    const { last_activity_at: lastActivityAt } = conversation;
    const hasLocalHydratedEmail = hasActiveRenderableEmailMessage({
      conversationId,
      message: data,
      store: this.app.$store,
    });
    const needsHydration =
      isEmailMessageMissingHydratedBody(data) && !hasLocalHydratedEmail;
    const messageForStore = needsHydration
      ? { ...data, hydrationPending: true }
      : data;
    this.app.$store.dispatch('addMessage', messageForStore);
    // Aggiorna last_activity_at solo se abbiamo dati validi
    if (lastActivityAt && conversationId) {
      this.app.$store.dispatch('updateConversationLastActivity', {
        lastActivityAt,
        conversationId,
      });
    }

    // Invalida cache liste conversazioni per inbox email quando arriva un INCOMING,
    // così cambiando/ritornando sulla label si vede l'ordine aggiornato senza stale cache.
    try {
      const isIncoming = isIncomingRealtimeMessage(data);

      const inboxId = resolveRealtimeInboxId(data);
      if (isIncoming && inboxId) {
        const inbox = this.app.$store.getters['inboxes/getInbox'](inboxId);
        const isEmail = inbox?.channel_type === 'Channel::Email';
        if (isEmail) {
          this.showNewEmailMessageBadge({
            conversationId,
            message: data,
          });
          this.app.$store.dispatch('clearConversationCache', { inboxId });
          this.scheduleConversationSnapshot(conversationId, {
            ...data,
            conversation: data?.conversation || {},
          });
          this.scheduleLabelsUnreadRefresh(inboxId);
        } else {
          // Inbox chat (WhatsApp & co.): se la conversazione non è nella lista
          // caricata, il solo addMessage non può mostrarla. Lo snapshot recupera
          // la conversazione completa e la inserisce in testa.
          const getConversationById =
            this.app.$store.getters.getConversationById;
          const conversationInList =
            typeof getConversationById === 'function'
              ? getConversationById(conversationId)
              : null;
          if (!conversationInList) {
            this.scheduleConversationSnapshot(conversationId, {
              ...data,
              conversation: data?.conversation || {},
            });
          }
        }
      }
    } catch (_) {
      // ignore cache invalidation errors on message
    }

    // Evidenziazione immediata "non letto" per messaggi in arrivo
    try {
      const isIncoming = isIncomingRealtimeMessage(data);
      if (isIncoming) {
        this.app.$store.commit('UPDATE_MESSAGE_UNREAD_COUNT', {
          id: conversationId,
          lastSeen: null,
          messageCreatedAt: data?.created_at,
          source: 'incoming-message',
          unreadCount: data?.conversation?.unread_count ?? 1,
        });
      }
    } catch (_) {
      // ignore optimistic unread update errors
    }

    // Aggiornamento ottimistico pallino "non letti" su inbox email
    try {
      const isIncomingMsg = isIncomingRealtimeMessage(data);

      if (isIncomingMsg) {
        const msgInboxId = resolveRealtimeInboxId(data);
        if (msgInboxId) {
          const inboxObj =
            this.app.$store.getters['inboxes/getInbox'](msgInboxId);
          if (inboxObj?.channel_type === 'Channel::Email') {
            // Marca la label INBOX come has_unread ottimisticamente
            const cachedLabels =
              this.app.$store.getters['inboxes/getInboxLabels'](msgInboxId);
            const inboxLabel = cachedLabels.find(
              (l) => l.title && l.title.toUpperCase() === 'INBOX'
            );
            if (inboxLabel) {
              this.app.$store.commit('inboxes/SET_LABEL_UNREAD_STATUS', {
                inboxId: msgInboxId,
                labelId: inboxLabel.id,
                hasUnread: true,
              });
            }
          } else {
            // Per inbox non-email (WhatsApp, API, etc.): marca l'inbox stessa come has_unread
            this.app.$store.commit('inboxes/SET_INBOX_UNREAD_STATUS', {
              inboxId: msgInboxId,
              hasUnread: true,
            });
          }
        }
      }
    } catch (_) {
      // ignore optimistic inbox unread update errors
    }

    // Pre-fetch in background: carica messaggi completi per conversazioni NON attive
    // (il broadcast non include più il corpo email per ridurre carico Redis)
    try {
      const selected = this.app.$store.getters.getSelectedChat || {};
      const isActive =
        selected && Number(selected.id) === Number(conversationId);
      if (isActive && needsHydration) {
        // Conversazione attiva con email senza body → sync dello store
        this.scheduleActiveConversationSync(conversationId);
      } else if (!isActive) {
        // Conversazione NON attiva → pre-fetch nel cache
        this.schedulePrefetch(conversationId);
      }
    } catch (_) {
      // ignore prefetch scheduling errors
    }
  };

  showNewEmailBadgeForConversation = (conversation = {}) => {
    try {
      const latestMessage = Array.isArray(conversation?.messages)
        ? conversation.messages[conversation.messages.length - 1]
        : null;

      if (
        !isEmailRealtimeConversation({
          conversation,
          store: this.app.$store,
        }) ||
        !isIncomingRealtimeMessage(latestMessage)
      ) {
        return;
      }

      this.showNewEmailMessageBadge({
        conversationId: conversation.id,
        message: latestMessage,
      });
    } catch (_) {
      // ignore transient badge errors
    }
  };

  resolveNewEmailBadgeMessageForConversationUpdate = (conversation = {}) => {
    try {
      const getConversationById = this.app.$store.getters.getConversationById;
      const currentConversation =
        typeof getConversationById === 'function'
          ? getConversationById(conversation?.id)
          : null;

      if (
        !shouldMarkConversationUpdateAsNewEmail({
          currentConversation,
          incomingConversation: conversation,
          store: this.app.$store,
        })
      ) {
        return null;
      }

      const lastIncomingAt =
        resolveRealtimeConversationLastIncomingAt(conversation);
      return (
        resolveLatestIncomingRealtimeMessage(conversation) || {
          id: `${conversation.id}-${lastIncomingAt}`,
        }
      );
    } catch (_) {
      // ignore transient badge errors
      return null;
    }
  };

  showNewEmailBadgeForConversationUpdate = (conversation = {}) => {
    try {
      const message =
        this.resolveNewEmailBadgeMessageForConversationUpdate(conversation);
      if (!message) {
        return;
      }

      this.showNewEmailMessageBadge({
        conversationId: conversation.id,
        message,
      });
    } catch (_) {
      // ignore transient badge errors
    }
  };

  showNewEmailMessageBadge = ({ conversationId = null, message = {} } = {}) => {
    try {
      if (!conversationId) {
        return;
      }

      const messageId =
        message?.id || message?.echo_id || `${conversationId}-${Date.now()}`;
      const expiresAt = Date.now() + NEW_EMAIL_MESSAGE_BADGE_DURATION_MS;

      this.app.$store.commit(SET_CONVERSATION_NEW_MESSAGE_BADGE, {
        conversationId,
        messageId,
        expiresAt,
      });

      if (this.NewMessageBadgeTimers[conversationId]) {
        clearSafeTimeout(this.NewMessageBadgeTimers[conversationId]);
      }

      this.NewMessageBadgeTimers[conversationId] = scheduleSafeTimeout(() => {
        this.app.$store.commit(CLEAR_CONVERSATION_NEW_MESSAGE_BADGE, {
          conversationId,
          messageId,
        });
        this.NewMessageBadgeTimers[conversationId] = null;
      }, NEW_EMAIL_MESSAGE_BADGE_DURATION_MS);
    } catch (_) {
      // ignore transient badge errors
    }
  };

  scheduleActiveConversationSync = (
    conversationId,
    attempt = 0,
    expectedAccountId = null
  ) => {
    try {
      if (!conversationId || this.isDisconnected) return;

      const selected = this.app.$store.getters.getSelectedChat || {};
      const isActive =
        selected && Number(selected.id) === Number(conversationId);
      if (!isActive) return;

      const currentAccountId = this.app.$store.getters.getCurrentAccountId;
      const accountId = expectedAccountId ?? currentAccountId;
      const hasAccountInRoute = isAccountScopedLocation();

      if (
        expectedAccountId != null &&
        String(currentAccountId) !== String(expectedAccountId)
      ) {
        return;
      }

      if (!accountId || !hasAccountInRoute) {
        if (attempt < 3) {
          scheduleSafeTimeout(
            () =>
              this.scheduleActiveConversationSync(
                conversationId,
                attempt + 1,
                accountId
              ),
            200
          );
        }
        return;
      }

      const hydrationKey = buildRealtimeHydrationKey(accountId, conversationId);

      // Debounce per conversazione: cancella un timer esistente e programma una sync tra 250ms
      if (this.SyncTimers[hydrationKey]) {
        clearSafeTimeout(this.SyncTimers[hydrationKey]);
      }
      this.SyncTimers[hydrationKey] = scheduleSafeTimeout(() => {
        let shouldRetry = false;
        this.app.$store
          .dispatch('syncActiveConversationMessages', {
            accountId,
            conversationId,
          })
          .then((result) => {
            const activeConversation =
              this.app.$store.getters.getSelectedChat || {};
            const stillActive =
              Number(activeConversation.id) === Number(conversationId);
            const stillPending = (activeConversation.messages || []).some(
              (message) =>
                message?.hydrationPending === true ||
                message?.source === 'conversation_list'
            );
            shouldRetry =
              stillActive &&
              stillPending &&
              (result?.status === 'failed' ||
                result?.status === 'partial' ||
                result?.status === 'invalidated');
          })
          .catch(() => {
            shouldRetry = true;
          })
          .finally(() => {
            this.SyncTimers[hydrationKey] = null;
            if (shouldRetry && attempt < 3 && !this.isDisconnected) {
              const retryDelay = 500 * 2 ** attempt;
              this.SyncTimers[hydrationKey] = scheduleSafeTimeout(() => {
                this.SyncTimers[hydrationKey] = null;
                this.scheduleActiveConversationSync(
                  conversationId,
                  attempt + 1,
                  accountId
                );
              }, retryDelay);
            }
          });
      }, 250);
    } catch (_) {
      // ignora errori
    }
  };

  // Pianifica uno snapshot dei metadati della conversazione con debounce e cooldown per id
  // Refresh debounced delle label per aggiornare il pallino "non letti"
  // Max 1 refresh per inbox ogni 3 secondi
  scheduleLabelsUnreadRefresh = (inboxId) => {
    if (!inboxId) return;

    if (this.labelRefreshTimers[inboxId]) {
      clearSafeTimeout(this.labelRefreshTimers[inboxId]);
    }

    this.labelRefreshTimers[inboxId] = scheduleSafeTimeout(() => {
      this.app.$store
        .dispatch(INBOX_STORE_FETCH_INBOX_LABELS_ACTION, inboxId)
        .catch(() => {});
      this.labelRefreshTimers[inboxId] = null;
    }, 3000);
  };

  // Pulizia timer pendenti alla disconnessione per evitare leak
  disconnect(options) {
    this.isDisconnected = true;
    clearSafeTimeout(this.realtimeResyncTimer);
    this.realtimeResyncTimer = null;
    if (this.labelRefreshTimers) {
      Object.values(this.labelRefreshTimers).forEach((timer) =>
        clearSafeTimeout(timer)
      );
      this.labelRefreshTimers = {};
    }
    if (this.inboxRefreshTimer) {
      clearSafeTimeout(this.inboxRefreshTimer);
      this.inboxRefreshTimer = null;
    }
    if (this.SyncTimers) {
      Object.values(this.SyncTimers).forEach((timer) =>
        clearSafeTimeout(timer)
      );
      this.SyncTimers = {};
    }
    if (this.SnapshotTimers) {
      Object.values(this.SnapshotTimers).forEach((timer) =>
        clearSafeTimeout(timer)
      );
      this.SnapshotTimers = {};
    }
    if (this.PrefetchTimers) {
      Object.values(this.PrefetchTimers).forEach((timer) =>
        clearSafeTimeout(timer)
      );
      this.PrefetchTimers = {};
    }
    if (this.NewMessageBadgeTimers) {
      Object.values(this.NewMessageBadgeTimers).forEach((timer) =>
        clearSafeTimeout(timer)
      );
      this.NewMessageBadgeTimers = {};
    }
    if (this.CancelTyping) {
      this.CancelTyping.forEach((timer) => clearSafeTimeout(timer));
      this.CancelTyping.clear();
    }
    super.disconnect(options);
  }

  // Refresh debounced della lista inbox per aggiornare has_unread su inbox non-email
  scheduleInboxUnreadRefresh = () => {
    if (this.inboxRefreshTimer) {
      clearSafeTimeout(this.inboxRefreshTimer);
    }
    this.inboxRefreshTimer = scheduleSafeTimeout(() => {
      this.app.$store.dispatch('inboxes/refreshUnreadStatus').catch(() => {});
      this.inboxRefreshTimer = null;
    }, 3000);
  };

  scheduleConversationSnapshot = (
    conversationId,
    payload = {},
    attempt = 0
  ) => {
    try {
      if (!conversationId || this.isDisconnected) return;

      const accountId = this.app.$store.getters.getCurrentAccountId;
      const hasAccountInRoute = isAccountScopedLocation();

      // Boot race: se l'account non è ancora pronto o la route non è account scoped, ritenta
      if (!accountId || !hasAccountInRoute) {
        if (attempt < 3) {
          scheduleSafeTimeout(
            () =>
              this.scheduleConversationSnapshot(
                conversationId,
                payload,
                attempt + 1
              ),
            200
          );
        }
        return;
      }

      // Se il payload contiene già metadati utili, prova ad aggiornare direttamente la conversazione
      try {
        const convo = payload?.conversation || {};
        const hasUsefulFields =
          convo?.labels !== undefined ||
          convo?.label_list !== undefined ||
          convo?.label_ids !== undefined ||
          convo?.participants_summary !== undefined ||
          convo?.meta !== undefined ||
          convo?.additional_attributes !== undefined ||
          convo?.messages_count !== undefined ||
          convo?.unread_count !== undefined;

        if (convo?.id) {
          this.app.$store.dispatch(
            'updateConversation',
            this.buildRealtimeConversationUpdate(convo)
          );
        }

        // Se già abbiamo campi utili, possiamo evitare snapshot immediato
        if (hasUsefulFields) {
          return;
        }
      } catch (_) {
        // ignore payload snapshot shortcut errors
      }

      // Calcola se il messaggio è INCOMING; sugli INCOMING applichiamo un cooldown più breve
      const isIncoming =
        payload?.message_type === 0 ||
        payload?.message_type === 'incoming' ||
        payload?.message_type_before_type_cast === 0;

      // Cooldown per conversazione: ~7.5s default, ~1.5s per INCOMING
      const cooldownMs = isIncoming ? 1500 : 7500;
      const last = this.SnapshotCooldown[conversationId] || 0;
      if (Date.now() - last < cooldownMs) {
        return;
      }

      // Debounce: accorpa burst entro ~250ms
      if (this.SnapshotTimers[conversationId]) {
        clearSafeTimeout(this.SnapshotTimers[conversationId]);
      }
      this.SnapshotTimers[conversationId] = scheduleSafeTimeout(() => {
        this.app.$store
          .dispatch('getConversation', {
            conversationId,
            placementSource: CONVERSATION_PLACEMENT_SOURCE.REALTIME,
          })
          .catch(() => {})
          .finally(() => {
            this.SnapshotCooldown[conversationId] = Date.now();
            this.SnapshotTimers[conversationId] = null;
          });
      }, 200);
    } catch (_) {
      // ignora errori snapshot
    }
  };

  // Pre-fetch leggero dei messaggi per conversazioni NON attive.
  // Quando il broadcast non include il corpo email, questo carica i messaggi
  // completi in background così che setActiveChat li trovi in cache.
  schedulePrefetch = (conversationId, { bypassCooldown = false } = {}) => {
    try {
      if (!conversationId || this.isDisconnected) return;

      const accountId = this.app.$store.getters.getCurrentAccountId;
      const hasAccountInRoute = isAccountScopedLocation();
      if (!accountId || !hasAccountInRoute) return;
      const hydrationKey = buildRealtimeHydrationKey(accountId, conversationId);

      // Skip se la conversazione è già aperta
      const selected = this.app.$store.getters.getSelectedChat || {};
      if (selected && Number(selected.id) === Number(conversationId)) return;

      // Cooldown: max 1 prefetch ogni 5s per conversazione
      const lastPrefetch = this.PrefetchCooldown[hydrationKey] || 0;
      if (!bypassCooldown && Date.now() - lastPrefetch < 5000) return;

      // Debounce: accorpa burst entro 300ms
      if (this.PrefetchTimers[hydrationKey]) {
        clearSafeTimeout(this.PrefetchTimers[hydrationKey]);
      }

      const runPrefetch = () => {
        // Accoda invece di perdere l'evento quando tre hydration sono già in
        // volo. Il controllo dentro il callback rende effettivo il limite
        // anche quando molti timer scadono nello stesso tick.
        if (this.activePrefetchCount >= 3) {
          this.PrefetchTimers[hydrationKey] = scheduleSafeTimeout(
            runPrefetch,
            150
          );
          return;
        }

        this.activePrefetchCount += 1;
        this.PrefetchCooldown[hydrationKey] = Date.now();

        this.app.$store
          .dispatch('prefetchConversationMessages', {
            accountId,
            conversationId,
            force: true,
          })
          .catch(() => {})
          .finally(() => {
            this.activePrefetchCount = Math.max(
              0,
              this.activePrefetchCount - 1
            );
            this.PrefetchTimers[hydrationKey] = null;
          });
      };
      this.PrefetchTimers[hydrationKey] = scheduleSafeTimeout(runPrefetch, 300);
    } catch (_) {
      // ignora errori di prefetch scheduling
    }
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => safeReloadWindow();

  onStatusChange = (data) => {
    this.app.$store.dispatch(
      'updateConversation',
      this.buildRealtimeConversationUpdate(data)
    );
    if (data.archive_version != null) {
      const refreshStats = getWindowGlobal('updateConversationStats');
      if (typeof refreshStats === 'function') refreshStats();
    }
  };

  onConversationUpdated = (data) => {
    const currentConversation = findRealtimeConversation({
      conversationId: data?.id,
      store: this.app.$store,
    });
    const orderedData = preserveNewestAiScheduledState({
      currentConversation,
      incomingConversation: data,
    });
    // Letta PRIMA del dispatch: dopo, lo stato locale e' gia' quello nuovo.
    const affectsList = conversationUpdateAffectsList({
      currentConversation,
      incomingConversation: orderedData,
    });
    const conversationUpdate =
      this.buildRealtimeConversationUpdate(orderedData);
    const newEmailBadgeMessage =
      this.resolveNewEmailBadgeMessageForConversationUpdate(orderedData);
    this.app.$store.dispatch('updateConversation', conversationUpdate);
    if (newEmailBadgeMessage) {
      this.showNewEmailMessageBadge({
        conversationId: orderedData.id,
        message: newEmailBadgeMessage,
      });
    }

    // Per email un update puo cambiare lettura, folder o ordinamento lista:
    // solo allora la cache conversazioni va invalidata, poi il prefetch sotto
    // riallinea lo snapshot. Un update che muove i soli flag indicatore non
    // tocca ne' ordine ne' filtri, e la cache resta valida.
    try {
      const inboxId =
        orderedData?.inbox_id || orderedData?.inboxId || orderedData?.inbox?.id;
      if (inboxId && affectsList) {
        const inbox = this.app.$store.getters['inboxes/getInbox'](inboxId);
        const isEmail = inbox?.channel_type === EMAIL_CHANNEL_TYPE;
        if (isEmail) {
          this.app.$store.dispatch('clearConversationCache', { inboxId });
          // Refresh pallino non letti quando agent_last_seen_at cambia
          this.scheduleLabelsUnreadRefresh(inboxId);
        } else {
          // Per inbox non-email: refresh debounced dello stato unread
          this.scheduleInboxUnreadRefresh();
        }
      }
    } catch (_) {
      // ignore cache invalidation errors
    }

    // Pre-fetch per conversazioni email aggiornate
    try {
      const inboxId =
        orderedData?.inbox_id || orderedData?.inboxId || orderedData?.inbox?.id;
      const inboxForPrefetch = inboxId
        ? this.app.$store.getters['inboxes/getInbox'](inboxId)
        : null;
      const isEmail = inboxForPrefetch?.channel_type === EMAIL_CHANNEL_TYPE;
      if (isEmail && orderedData?.id) {
        this.schedulePrefetch(orderedData.id);
      }
    } catch (_) {
      // ignore email prefetch scheduling errors
    }
  };

  buildRealtimeConversationUpdate = (conversation, options = {}) => {
    try {
      const normalizedConversation =
        conversation?.participants_summary !== undefined &&
        conversation?.additional_attributes?.participants_summary === undefined
          ? {
              ...conversation,
              additional_attributes: {
                ...(conversation.additional_attributes || {}),
                participants_summary: conversation.participants_summary,
              },
            }
          : conversation;
      const getConversationById = this.app.$store.getters.getConversationById;
      const currentConversation =
        typeof getConversationById === 'function'
          ? getConversationById(normalizedConversation?.id)
          : null;

      const conversationUpdate = preserveRealtimeUnreadState({
        currentConversation,
        incomingConversation: normalizedConversation,
        messageCreatedAt: options.messageCreatedAt,
        source: options.source,
      });

      return {
        ...conversationUpdate,
        __placement_source: CONVERSATION_PLACEMENT_SOURCE.REALTIME,
        ...(options.messageCreatedAt !== undefined
          ? { __read_state_message_created_at: options.messageCreatedAt }
          : {}),
        ...(options.source ? { __read_state_source: options.source } : {}),
      };
    } catch (_) {
      return conversation && typeof conversation === 'object'
        ? {
            ...conversation,
            __placement_source: CONVERSATION_PLACEMENT_SOURCE.REALTIME,
          }
        : conversation;
    }
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId, user);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.syncConversationPlacementFreeze(conversation);
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId, user);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
    this.syncConversationPlacementFreeze(conversation);
  };

  // Otello al lavoro su una inbox API: la lista tiene ferma la riga finche'
  // il typing e' acceso e la colloca una volta sola allo spegnimento (anche
  // quello automatico del timer qui sotto). Regola in conversationFrozenPlacement.
  syncConversationPlacementFreeze = (conversation = {}) => {
    this.app.$store.dispatch('syncConversationPlacementFreeze', {
      conversationId: conversation.id,
      inboxId: conversation.inbox_id ?? null,
    });
  };

  clearTimer = (conversationId, user) => {
    const key = typingTimerKey(conversationId, user);
    const timerEvent = this.CancelTyping.get(key);

    if (timerEvent) {
      clearSafeTimeout(timerEvent);
    }
    this.CancelTyping.delete(key);
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping.set(
      typingTimerKey(conversationId, user),
      scheduleSafeTimeout(() => {
        this.onTypingOff({ conversation, user });
      }, 30000)
    );
  };

  onContactDelete = (data) => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
  };

  onContactUpdate = (data) => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onCacheInvalidate = (data) => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
  };
}

export default {
  init(pubsubToken) {
    return new ActionCableConnector(getWindowGlobal('OTELLO_APP'), pubsubToken);
  },
};
